import type { UseMutationResult, UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { CACHE_KEY_DEEPLINK_FOR_ADD_ADOPTION } from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';

export enum PrecedingIconType {
  coupon = 'coupon',
}

const ONE_MINUTE = 1000 * 60;

export interface GetDeeplinkForAppAdoption {
  userUuid: string;
  isAuthenticated: boolean;
}

export interface GetDeeplinkResponse {
  url: string;
}

export enum DeepLinkCampaign {
  app_adoption_interstitial = 'app_adoption_interstitial',
  app_adoption_order_tracking = 'app_adoption_order_tracking',
  mobile_first_time_checkout = 'mobile_first_time_checkout',
}

export enum DeepLinkChannel {
  sms = 'sms',
  email = 'email',
}

export const useGetDeeplinkForAppAdoption = (
  deeplinkPath: string,
  campaign: DeepLinkCampaign,
  channel: DeepLinkChannel,
  options?: Partial<UseQueryOptions<GetDeeplinkResponse, AxiosError>>
) => {
  const authClient = useBffClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useQuery<GetDeeplinkResponse, AxiosError>(
    [CACHE_KEY_DEEPLINK_FOR_ADD_ADOPTION, accountId, deeplinkPath, campaign],
    () =>
      authClient
        .post(`/deeplinks`, {
          userUuid: loggedInUser?.uuid,
          isAuthenticated: true,
          deeplinkPath: deeplinkPath,
          campaign,
          channel,
        })
        .then(resp => resp.data),
    {
      ...options,
      enabled: !!accountId && options?.enabled !== false,
      refetchOnMount: 'always',
      refetchInterval: ONE_MINUTE,
    }
  );
};

export interface CreateDeeplinkRequest {
  deeplinkPath: string;
  campaign: DeepLinkCampaign;
  channel: DeepLinkChannel;
  userUuid: string;
  skipLogoutOnError: boolean;
}

export const useCreateDeeplink = (): UseMutationResult<GetDeeplinkResponse, AxiosError, CreateDeeplinkRequest> => {
  const authClient = useBffClient();
  const queryClient = useQueryClient();

  return useMutation(
    (request: CreateDeeplinkRequest) =>
      authClient
        .post(
          `/deeplinks`,
          {
            userUuid: request.userUuid,
            isAuthenticated: true,
            deeplinkPath: request.deeplinkPath,
            campaign: request.campaign,
            channel: request.channel,
          },
          {
            headers: {
              'x-skip-logout': request.skipLogoutOnError,
            },
          }
        )
        .then(resp => resp.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_DEEPLINK_FOR_ADD_ADOPTION]);
      },
    }
  );
};
